import { useEffect } from 'react';
import { useTour } from '@reactour/tour';
import { getActiveTourKey } from './activeTour';
import { CANPARK_MONITOR_STEP } from './tourRegistry';

const VIEW_BTN_SELECTOR = '[data-tour="view-site-button"]';

// Action-driven gates for the CANpark "Monitor your site" tour. Runs inside
// CANparkAdmin — i.e. only once we're on /canpark-admin — so its presence is the
// signal that the user navigated here. Gates:
//   MANAGE_SITES_ITEM -> VIEW_BUTTON   once a lot's "View" button has rendered
//   VIEW_BUTTON        -> MONITOR_INTRO the per-lot monitoring view opened
// The remaining steps (filter, views) are Next-driven. The sidebar menu steps
// (OPEN_CANPARK -> MANAGE_SITES_ITEM) are handled by useSidebarTourControl.
export const useMonitorSiteTourSync = (viewOpen: boolean) => {
	const { isOpen, currentStep, setCurrentStep } = useTour();

	useEffect(() => {
		if (!isOpen || getActiveTourKey() !== 'canpark_monitor_site') return;

		if (currentStep === CANPARK_MONITOR_STEP.MANAGE_SITES_ITEM) {
			// Lots load asynchronously, so wait for the first card's View button to
			// exist before advancing — otherwise the VIEW_BUTTON step activates with
			// no anchor and its popover never shows. querySelector returns the first
			// lot's button, so that's the one the step targets.
			const found = () => !!document.querySelector(VIEW_BTN_SELECTOR);
			if (found()) {
				setCurrentStep(CANPARK_MONITOR_STEP.VIEW_BUTTON);
				return;
			}
			const observer = new MutationObserver(() => {
				if (found()) {
					observer.disconnect();
					setCurrentStep(CANPARK_MONITOR_STEP.VIEW_BUTTON);
				}
			});
			observer.observe(document.body, { childList: true, subtree: true });
			return () => observer.disconnect();
		}

		if (currentStep === CANPARK_MONITOR_STEP.VIEW_BUTTON && viewOpen) {
			setCurrentStep(CANPARK_MONITOR_STEP.MONITOR_INTRO);
		}
	}, [isOpen, currentStep, viewOpen, setCurrentStep]);
};
